import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import InterviewView from '../components/InterviewView';
import type { InterviewInfo } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://shandong-interview-api.fly.dev/api';

function InterviewPage() {
  const { token: urlToken } = useParams<{ token: string }>();
  const [token, setToken] = useState('');
  const [interviewData, setInterviewData] = useState<InterviewInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get token from URL parameter, query string, or use default
    let accessToken = urlToken;

    // If not in URL path, try query parameter
    if (!accessToken) {
      const urlParams = new URLSearchParams(window.location.search);
      accessToken = urlParams.get('token') || undefined;
    }

    // Use default token if none found (for development)
    if (!accessToken) {
      accessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjYW5kaWRhdGVfaWQiOjIsImpvYl9pZCI6MiwiZXhwIjoxNzYzODUzMjA0LCJ0eXBlIjoiY2FuZGlkYXRlX2FjY2VzcyJ9.mr4HsMsLmD5mIarcGBgHaff5dETR4IvHkZRUvzpFXQU';
    }

    setToken(accessToken);
    loadInterviewData(accessToken);
  }, [urlToken]);

  const loadInterviewData = async (accessToken: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/interview/${accessToken}/info`);

      if (!response.ok) {
        throw new Error('Failed to load interview data');
      }

      const data = await response.json();
      setInterviewData(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load interview data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-5">
        <div className="text-center p-10 bg-white rounded-lg shadow-md flex flex-col items-center gap-5">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin" />
          <p className="text-gray-600 text-sm">Loading interview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-5">
        <div className="text-center p-10 bg-white rounded-lg shadow-md">
          <h2 className="text-danger text-xl font-semibold mb-3">Error</h2>
          <p className="text-gray-700 mb-2">{error}</p>
          <p className="text-gray-600 text-sm">Please check your access link</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <InterviewView
        interviewData={interviewData}
        token={token}
        apiBaseUrl={API_BASE_URL}
      />
    </div>
  );
}

export default InterviewPage;
